// multiRecorder.js — the MultiRecord++ recorder (web).
//
// A fork, not a wrapper. api/Recorder.js is a full UI component (mic picker,
// wave, buttons, modals) that MultiRecordEngine was mounting headless at 0x0
// just to get its capture loop; this is that loop and nothing else. The classic
// recorder is untouched and still serves single-visit recording, the Chrome
// extension and the native bridge.
//
// Three things it does that the classic recorder does not:
//
//   * Cuts chunks on a silence gap near the 2-minute mark instead of exactly at
//     2:00, so a chunk boundary lands between sentences rather than through one.
//   * Freezes the target set at chunk START. The classic recorder reads its
//     conversationIds ref at UPLOAD time, so audio spoken entirely under {A} was
//     submitted as {A,B} whenever the set changed mid-chunk.
//   * Drops headerless blobs instead of shipping them. The stop-then-start race
//     produces bytes no backend can decode; the classic recorder uploads them,
//     gets "Invalid audio format" back, and plays the 880Hz ding — which is what
//     the "random beeps on start/stop" actually are.
//
// What it deliberately keeps from the classic recorder, because it is proven:
// the strictly serial upload queue, 3 retries, and waiting on `online` rather
// than dropping a chunk when the network goes away.
//
// This class is the WEB implementation of MultiRecord++ recording. It replaces
// the old multi-record path outright — there is no flag. On native,
// Native_Recorder posts to the same endpoint instead, because opening a second
// getUserMedia in the WebView fights the single iOS AVAudioSession and kills
// the recording.
import { looksLikeMediaContainer } from './audioChunk';
import { MultiRecordSession } from './multiRecordSession';
import { logAttribution } from './logAttribution';

// Chunk timing. Never cut before the classic 120s, then take the first sentence
// gap up to a 140s ceiling. The extra 20s is a wide net for a 750ms pause —
// conversation is full of them — and it cannot run away because the ceiling is
// hard. The payload stays far inside the inline limit either way (see
// MAX_INLINE_BLOB_BYTES), which is what makes the wider window safe.

// Do not cut a chunk younger than this on a retarget. Without it, two schedule
// changes in quick succession produce a sub-second chunk, which the voiced-time
// gate below then discards as silence — losing whatever was said in it. The
// classic recorder has the same 5s guard (Recorder.js, elapsedTime >= 5000).
// Cost: up to 5s of audio stays attributed to the previous target set, because
// chunkTargets is frozen at chunk start.
export {
  CHUNK_MIN_MS,
  CHUNK_HARD_MS,
  CHUNK_SILENCE_MS,
  MIN_RETARGET_CUT_MS,
  MIN_VOICED_MS,
} from './multiRecordSession';

// A chunk that somehow grows past this is not shippable inline: API Gateway
// caps a request at 10MB and base64 inflates by 4/3. The time ceiling above
// should make this unreachable (~2MB raw at 120s), so hitting it means the
// cut logic broke — say so loudly rather than firing a doomed request.
export const MAX_INLINE_BLOB_BYTES = 6 * 1024 * 1024;

// Recorders that still hold their unload guard. The embed's compliance rail
// reloads the panel to open a visit, and must not while one of these is busy.
const liveRecorders = new Set();

/** True while any recorder is capturing or still has audio to upload. */
export function multiRecorderBusy() {
  for (const recorder of liveRecorders) {
    if (!recorder.stopped || recorder.stopPending || recorder.draining || recorder.queue.length) {
      return true;
    }
  }
  return false;
}

// VAD, same shape as the ambient runtime's.
export const VAD_TICK_MS = 150;
export const RMS_SPEECH = 0.012;

const MAX_RETRIES = 3;

// Every drop/skip path logs under this tag. Session replay captures console
// output, so these lines are the only way to reconstruct why a visit came back
// short — there is no UI surface for a failed chunk.
const LOG = '[MR++] [Recorder]';

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export class MultiRecorder extends MultiRecordSession {
  constructor({
    apiClient,
    context,
    onMicLevel,
    onUtterances,
    onError,
    onAttributed,
  } = {}) {
    super();
    this.apiClient = apiClient;
    // MultiRecordContext — the sliding window. Read at UPLOAD time, not capture
    // time: the queue is serial, so by then it holds every chunk before this one.
    this.context = context;
    this.onMicLevel = onMicLevel || (() => {});
    this.onUtterances = onUtterances || (() => {});
    this.onError = onError || (() => {});
    // Only the classic Multi Recorder page passes this: it gets every chunk's
    // result, and the backend may hold back lines it cannot place. Without it
    // the backend must place every line, exactly as before.
    this.onAttributed = onAttributed || null;

    this.stopped = true;
    this.captureGeneration = 0;
    this.stream = null;
    this.captureStreams = [];
    this.audioCtx = null;
    this.analyser = null;
    this.recorder = null;
    this.stopPending = false;
    this.vadTimer = null;
    this.parts = [];

    this.queue = [];
    this.draining = false;
    this.disposed = false;
    this.onlineListener = null;
    // Closing the tab with chunks still queued loses them outright — the same
    // guard both classic recorders take while uploading.
    this.beforeUnload = (e) => {
      if (!this.queue.length && !this.stopPending) return undefined;
      e.preventDefault();
      e.returnValue = true;
      return true;
    };
    window.addEventListener('beforeunload', this.beforeUnload);
    liveRecorders.add(this);
  }

  /**
   * Release the unload guard. The recorder is unusable afterwards.
   *
   * stop() enqueues the final chunk from onstop, which fires asynchronously, so
   * the guard has to outlive this call — dropping it here would let a tab close
   * during that last upload lose the chunk silently. _drain releases it once
   * the queue is actually empty.
   */
  dispose() {
    this.disposed = true;
    this.stop();
    if (!this.stopPending && !this.queue.length && !this.draining) {
      this._releaseUnloadGuard();
    }
  }

  _releaseUnloadGuard() {
    window.removeEventListener('beforeunload', this.beforeUnload);
    liveRecorders.delete(this);
  }

  async start(targets, { deviceId, telehealth = false, onEnded } = {}) {
    if (this.disposed) return;
    if (!this.stopped) {
      console.log(`${LOG} start ignored — recorder is already running`);
      return;
    }
    this.stopped = false;
    this.targets = [...(targets || [])];
    const generation = ++this.captureGeneration;
    try {
      // The old onstop still reads its chunk metadata and parts. Finish it
      // before a new session can replace either or open another encoder.
      if (this.stopComplete) await this.stopComplete;
      if (generation !== this.captureGeneration) return;
      this._startRecordingSession(this.targets);
      console.log(`${LOG} requesting microphone for targets [${this.targets}]`);
      let displayStream;
      if (telehealth) {
        if (!navigator.mediaDevices.getDisplayMedia) {
          throw new Error('Call-tab audio sharing is not supported in this browser.');
        }
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        if (generation !== this.captureGeneration) {
          displayStream.getTracks().forEach((track) => track.stop());
          return;
        }
        this.captureStreams.push(displayStream);
        if (!displayStream.getAudioTracks().length) {
          throw new Error('No call audio was shared. Choose your call tab and enable audio sharing.');
        }
        displayStream.getTracks().forEach((track) => {
          track.addEventListener('ended', () => {
            if (generation !== this.captureGeneration) return;
            this.stop();
            onEnded?.();
          }, { once: true });
        });
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId && deviceId !== 'default' ? { deviceId: { exact: deviceId } } : true,
      });
      if (generation !== this.captureGeneration) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.stream = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      this.audioCtx = audioCtx;
      if (displayStream) {
        this.captureStreams.push(stream);
        const destination = audioCtx.createMediaStreamDestination();
        audioCtx.createMediaStreamSource(displayStream).connect(destination);
        audioCtx.createMediaStreamSource(stream).connect(destination);
        this.stream = destination.stream;
      }
      // Autoplay policy (and iOS especially) can hand back a suspended context.
      // A suspended analyser reads as pure silence, which the voiced-time gate
      // below would read as "nobody spoke" and discard every chunk.
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume().catch(() => {});
      }
      if (generation !== this.captureGeneration) return;
      if (displayStream && audioCtx.state !== 'running') {
        throw new Error('Could not activate call audio mixing. Start recording again.');
      }
      // Analyse the same mixed stream we record, so remote-only speech is kept.
      const source = audioCtx.createMediaStreamSource(this.stream);
      this.analyser = audioCtx.createAnalyser();
      this.analyser.fftSize = 2048;
      source.connect(this.analyser);

      this._startRecorder();
      this.vadTimer = setInterval(() => this._tick(), VAD_TICK_MS);
      console.log(
        `${LOG} microphone ready; VAD running every ${VAD_TICK_MS}ms`,
      );
    } catch (error) {
      if (generation !== this.captureGeneration) return;
      // Leaving `stopped` false here would wedge the recorder for the rest of
      // the day: the engine only calls start() again when it reads stopped, so
      // one busy-device failure would silently record nothing while the
      // timeline still showed the visit as live.
      this.stop();
      throw error;
    }
  }

  /**
   * The auto-switcher changed who is being recorded. Cut now so the new set
   * takes effect in seconds — the chunk being cut still carries the OLD set,
   * which is what it was actually spoken under.
   */
  setTargets(targets) {
    const reason = this.updateTargets(targets, !this.stopped && !!this.recorder);
    if (reason) this._cut(reason);
  }

  stop() {
    this.captureGeneration += 1;
    const chunkAgeMs = this.chunkStartedAt ? Date.now() - this.chunkStartedAt : 0;
    console.log(
      `${LOG} stop requested; chunk age=${chunkAgeMs}ms, targets=[${this.chunkTargets}], recorder=${this.recorder?.state || 'none'}`,
    );
    this.stopped = true;
    this.retargetPending = false;
    clearInterval(this.vadTimer);
    this.vadTimer = null;
    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.stopPending = true;
        this.recorder.stop(); // onstop still enqueues the final chunk
      }
    } catch (e) {
      // already torn down
      this.stopPending = false;
    }
    this.recorder = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.captureStreams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    this.captureStreams = [];
    this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
    this.analyser = null;
    this.onMicLevel(0);
  }

  _startRecorder() {
    this.parts = [];
    this.beginChunk();
    console.log(`${LOG} started new chunk for targets [${this.chunkTargets}]`);
    const recorder = new MediaRecorder(this.stream);

    // Resolved at the very end of onstop so a cut can await the stop fully
    // settling before the next recorder is created. Recreating it in the same
    // tick races the encoder teardown and yields headerless chunks.
    //
    // The resolver is a LOCAL, so each recorder resolves its own promise. Held
    // on `this` it could be orphaned by a stop()/start() landing inside the
    // onstop window, wiring the old recorder's onstop to the new recorder's
    // promise — which resolves the guard early and makes every later chunk
    // headerless, permanently.
    let resolveStopComplete;
    this.stopComplete = new Promise((resolve) => {
      resolveStopComplete = resolve;
    });

    this.recorder = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.parts.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(this.parts, {
        type: recorder.mimeType || 'audio/webm',
      });
      if (this.shouldKeepChunk() && blob.size > 0) {
        const metadata = this.chunkMetadata();
        console.log(
          `${LOG} chunk cut: ${metadata.durationSec}s, ${blob.size}B, voiced ${this.voicedMs}ms, targets [${this.chunkTargets}]`,
        );
        this._enqueue({
          blob,
          ...metadata,
          // When it was spoken, for the unplaced-line review.
          chunkStartedAt: this.chunkStartedAt,
          retries: 0,
        });
      } else {
        console.log(
          `${LOG} chunk skipped: ${Math.round((Date.now() - this.chunkStartedAt) / 1000)}s, ${blob.size}B, voiced ${this.voicedMs}ms (silence)`,
        );
      }
      this.stopPending = false;
      resolveStopComplete();
      if (this.disposed && !this.queue.length && !this.draining) {
        this._releaseUnloadGuard();
      }
    };
    try {
      recorder.start(1000); // 1s timeslices — Safari needs them
    } catch (error) {
      resolveStopComplete();
      throw error;
    }
  }

  async _cut(reason = 'requested') {
    if (!this.recorder || this.recorder.state !== 'recording') return;
    this.prepareCut(reason);
    console.log(
      `${LOG} cutting chunk (${reason}); age=${Date.now() - this.chunkStartedAt}ms, targets=[${this.chunkTargets}]`,
    );
    const settled = this.stopComplete;
    const generation = this.captureGeneration;
    try {
      this.stopPending = true;
      this.recorder.stop();
    } catch (e) {
      this.stopPending = false;
      this.keepShortCurrentChunk = false;
      return;
    }
    await settled;
    if (!this.stopped && generation === this.captureGeneration) {
      this._startRecorder();
    }
  }

  _tick() {
    if (this.stopped || !this.analyser) return;
    const buf = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    this.onMicLevel(rms);

    this.recordSpeech(rms >= RMS_SPEECH, VAD_TICK_MS);
    const reason = this.cutReason();
    if (reason) this._cut(reason);
  }

  _enqueue(item) {
    this.queue.push(item);
    console.log(
      `${LOG} queued ${item.durationSec}s chunk for [${item.targets}]; queue depth=${this.queue.length}`,
    );
    if (!this.draining) this._drain();
  }

  async _drain() {
    if (!navigator.onLine) {
      this._resumeWhenOnline();
      return;
    }
    this.draining = true;
    while (this.queue.length) {
      const item = this.queue[0];
      try {
        await this._upload(item);
        this.queue.shift();
      } catch (error) {
        if (String(error).includes('Invalid audio format')) {
          // Undecodable — retrying cannot help.
          console.error(
            `${LOG} backend rejected a chunk as undecodable; discarding it:`,
            error,
          );
          this.queue.shift();
          this.onError(error);
        } else if (!navigator.onLine) {
          // The network can disappear after the drain's preflight check.
          // Park without spending a retry and wait for it to return.
          console.warn(
            `${LOG} offline — parking ${this.queue.length} chunk(s) until the network returns`,
          );
          this.draining = false;
          this._resumeWhenOnline();
          return;
        } else if (item.retries < MAX_RETRIES) {
          item.retries += 1;
          console.warn(
            `${LOG} upload failed, retry ${item.retries}/${MAX_RETRIES} for targets [${item.targets}]:`,
            error,
          );
        } else {
          console.error(
            `${LOG} GAVE UP after ${MAX_RETRIES} retries — ${item.durationSec}s of audio for targets [${item.targets}] is lost:`,
            error,
          );
          this.queue.shift();
          this.onError(error);
        }
      }
    }
    this.draining = false;
    if (this.disposed && !this.stopPending) this._releaseUnloadGuard();
  }

  _resumeWhenOnline() {
    if (this.onlineListener) return;
    this.onlineListener = () => {
      console.log(`${LOG} network is back — resuming uploads`);
      this.onlineListener = null;
      if (!this.draining) this._drain();
    };
    window.addEventListener('online', this.onlineListener, { once: true });
  }

  async _upload(item) {
    // The stop→restart race can still emit a non-empty blob with no container
    // header. Every backend rejects it, so drop it here instead of spending a
    // round trip and surfacing an error to the clinician.
    if (!(await looksLikeMediaContainer(item.blob))) {
      console.warn(
        `${LOG} dropped an undecodable chunk (${item.blob.size}B, no container header), targets [${item.targets}]`,
      );
      return;
    }
    if (item.blob.size > MAX_INLINE_BLOB_BYTES) {
      // No presigned fallback exists for a multi-target chunk: the segmenter
      // needs one audio stream attributed across N visits, and the large-file
      // route (requesturl -> addfromurl) is single-conversation only. The time
      // ceiling makes this unreachable, so reaching it means the cut logic broke.
      throw new Error(
        `Chunk too large to upload inline (${item.blob.size}B > ${MAX_INLINE_BLOB_BYTES}B) — check the chunk-cut logic`,
      );
    }
    const audio = await blobToDataURL(item.blob);

    // Context is read HERE, not at capture: the queue is serial, so everything
    // ahead of this chunk has already been attributed and appended.
    // The chunk's own targets are the visits recording now, so a visit resumed
    // after a long gap keeps its anchor instead of ageing out mid-session.
    const contextPayload = this.context
      ? this.context.payload(Date.now(), item.targets)
      : [];
    console.log(
      `${LOG} POST /audio/addSegmented: targets=[${item.targets}], duration=${item.durationSec}s, bytes=${item.blob.size}, context=${contextPayload.length}, attempt=${item.retries + 1}`,
    );
    const data = await this.apiClient.addConversationAudioSegmented(
      item.targets,
      audio,
      item.durationSec,
      contextPayload,
      {
        recordingSessionId: item.recordingSessionId,
        chunkSeq: item.chunkSeq,
        chunkOffsetMs: item.chunkOffsetMs,
        visitJoins: item.visitJoins,
      },
      Boolean(this.onAttributed),
    );
    if (data && data.error) throw new Error(data.error);

    const utterances = (data && data.utterances) || [];
    console.log(
      `${LOG} /audio/addSegmented succeeded: ${utterances.length} utterance(s) across [${item.targets}] (${item.durationSec}s, context ${contextPayload.length} entries)`,
    );
    logAttribution('segmenter', data, item.targets);
    if (this.context) this.context.append(utterances);
    this.onUtterances(utterances);
    if (this.onAttributed) {
      this.onAttributed(data, item.targets, item.chunkStartedAt);
    }
  }
}

export default MultiRecorder;
